import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Globe, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { getText } from '../utils/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PartnerDetailPage = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchPartner();
  }, [id]);

  const fetchPartner = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/partners/${id}`);
      if (response.ok) {
        const data = await response.json();
        setPartner(data);
      }
    } catch (err) {
      console.error('Error fetching partner:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get text based on language
  const getPartnerText = (fieldEn, fieldFr, fieldAr) => {
    if (language === 'ar' && fieldAr) return fieldAr;
    if (language === 'fr' && fieldFr) return fieldFr;
    return fieldEn || fieldFr || '';
  };

  const getDescription = () => {
    return getPartnerText(
      partner?.description_en,
      partner?.description_fr,
      partner?.description_ar
    );
  };

  const getFullDescription = () => {
    return getPartnerText(
      partner?.full_description_en,
      partner?.full_description_fr,
      partner?.full_description_ar
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  if (!partner) {
    return (
      <Layout>
        <div className="py-32 text-center">
          <h1 className="text-2xl font-light text-slate-800">
            {getText(language, 'Partner not found', 'Partenaire non trouvé', 'الشريك غير موجود')}
          </h1>
          <Link to="/partners" className="mt-4 inline-block">
            <Button variant="outline" className="rounded-full">
              <ArrowLeft className="mr-2" size={16} />
              {getText(language, 'Back to Partners', 'Retour aux Partenaires', 'العودة إلى الشركاء')}
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const fullDesc = getFullDescription();
  const shortDesc = getDescription();

  return (
    <Layout>
      {/* Page Header */}
      <section className="pt-20 pb-16 bg-slate-100">
        <div className="container mx-auto px-6">
          <Link 
            to="/partners" 
            className="inline-flex items-center text-sm text-slate-400 hover:text-slate-600 mb-8 transition-colors"
          >
            <ArrowLeft className="mr-2" size={16} />
            {getText(language, 'Back to Partners', 'Retour aux Partenaires', 'العودة إلى الشركاء')}
          </Link>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <img
                src={partner.logo}
                alt={partner.name}
                className="h-20 w-auto"
              />
            </div>
            <div>
              <h1 className="text-5xl font-light text-slate-800 mb-4">
                {partner.name}
              </h1>
              <div className="flex items-center gap-6">
                <span className="flex items-center gap-2 text-sm text-slate-500">
                  <Globe size={16} />
                  {partner.country}
                </span>
                {partner.website && (
                  <a 
                    href={partner.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-slate-800 hover:text-slate-600 transition-colors"
                  >
                    <ExternalLink size={16} />
                    {getText(language, 'Visit Website', 'Visiter le Site', 'زيارة الموقع')}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <h2 className="text-2xl font-light text-slate-800 mb-6">
                  {getText(language,
                    `SAE is the Official Representative of ${partner.name} in Algeria`,
                    `SAE est Représentant Officiel de ${partner.name} en Algérie`,
                    `SAE هي الممثل الرسمي لـ ${partner.name} في الجزائر`
                  )}
                </h2>
                
                {/* Full description or short description */}
                <div className="text-slate-600 whitespace-pre-line leading-relaxed">
                  {fullDesc || shortDesc || getText(language,
                    'Contact us for more information about this partner.',
                    'Contactez-nous pour plus d\'informations sur ce partenaire.',
                    'اتصل بنا للحصول على مزيد من المعلومات حول هذا الشريك.'
                  )}
                </div>

                {/* Video if available */}
                {partner.video_id && (
                  <div className="mt-8">
                    <h3 className="text-lg font-medium text-slate-800 mb-4">
                      {getText(language, 'Video', 'Vidéo', 'فيديو')}
                    </h3>
                    <div className="aspect-video rounded-xl overflow-hidden bg-slate-800">
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${partner.video_id}`}
                        title={partner.name}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Benefits Section */}
              <div className="bg-white rounded-2xl p-8 shadow-sm mt-6">
                <h3 className="text-xl font-medium text-slate-800 mb-4">
                  {getText(language,
                    `Why choose ${partner.name}?`,
                    `Pourquoi choisir ${partner.name} ?`,
                    `لماذا تختار ${partner.name}؟`
                  )}
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-slate-600">
                      {getText(language,
                        `Get information about ${partner.name} products (Applications, Specifications...)`,
                        `Avoir des informations concernant les produits ${partner.name} (Applications, Caractéristiques...)`,
                        `الحصول على معلومات حول منتجات ${partner.name} (التطبيقات، المواصفات...)`
                      )}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-slate-600">
                      {getText(language,
                        `Get the best prices on ${partner.name} products`,
                        `Avoir les meilleurs prix sur les produits ${partner.name}`,
                        `الحصول على أفضل الأسعار على منتجات ${partner.name}`
                      )}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-slate-600">
                      {getText(language,
                        `Get the best delivery times on ${partner.name} products`,
                        `Avoir les meilleurs délais de livraison sur les produits ${partner.name}`,
                        `الحصول على أفضل مواعيد التسليم على منتجات ${partner.name}`
                      )}
                    </span>
                  </li>
                </ul>
                <p className="mt-6 text-slate-500 italic">
                  {getText(language,
                    'SAE AGRO EQUIPEMENT will offer you its expertise in the matter.',
                    'AGRO EQUIPEMENT vous offrira ses compétences en la matière.',
                    'SAE AGRO EQUIPEMENT ستقدم لكم خبرتها في هذا المجال.'
                  )}
                </p>
              </div>
            </div>

            {/* Sidebar Images */}
            <div className="space-y-4">
              {partner.images && partner.images.length > 0 ? (
                partner.images.map((image, index) => (
                  <div 
                    key={index}
                    className="cursor-pointer group relative overflow-hidden rounded-xl bg-white shadow-sm"
                    onClick={() => setSelectedImage(image)}
                  >
                    <img
                      src={image}
                      alt={`${partner.name} ${index + 1}`}
                      className="w-full rounded-xl transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 text-white bg-black/50 px-3 py-1 rounded-full text-sm">
                        {getText(language, 'View', 'Voir', 'عرض')}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-xl p-8 text-center shadow-sm">
                  <p className="text-slate-400">
                    {getText(language, 'No images available', 'Aucune image disponible', 'لا توجد صور متاحة')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-100">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-xl mx-auto">
            <h2 className="text-2xl font-light text-slate-800 mb-4">
              {getText(language,
                `Interested in ${partner.name} Products?`,
                `Intéressé par les Produits ${partner.name} ?`,
                `مهتم بمنتجات ${partner.name}؟`
              )}
            </h2>
            <p className="text-slate-500 mb-8">
              {getText(language,
                'Contact us for information, pricing, and delivery times. We are at your service for any information you need.',
                'Contactez-nous pour des informations, des prix et des délais de livraison. Nous restons à votre service pour toutes informations.',
                'اتصل بنا للحصول على معلومات وأسعار ومواعيد التسليم. نحن في خدمتكم لأي معلومات تحتاجونها.'
              )}
            </p>
            <Link to="/contact">
              <Button className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-6 text-sm rounded-full">
                {getText(language, 'Contact Us', 'Contactez-Nous', 'اتصل بنا')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Image Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-slate-900 border-none overflow-hidden rounded-2xl">
          <DialogTitle className="sr-only">Image preview</DialogTitle>
          {selectedImage && (
            <img
              src={selectedImage}
              alt={partner.name}
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default PartnerDetailPage;
